<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Contact form posting to a tenant-configured external endpoint. Fields are
 * repeatable so each tenant collects what they actually need; `required` and
 * `type` map onto native validation, so no JavaScript validates anything here.
 *
 * As with NewsletterBlock, submissions never touch this platform.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const submitted = ref(false)

const fields = computed(() => props.data.fields ?? [])

const inputClass
  = 'w-full rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-bg)] px-4 py-2.5 text-[var(--site-fg)]'
</script>

<template>
  <div
    v-if="data.action_url"
    class="mx-auto max-w-xl"
  >
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.headline"
      :subtitle="data.subtext"
    />

    <form
      :action="data.action_url"
      :method="data.method || 'post'"
      :target="data.target === 'self' ? '_self' : '_blank'"
      rel="noopener"
      accept-charset="UTF-8"
      class="mt-6 space-y-4"
      @submit="submitted = true"
    >
      <input
        v-for="(hidden, i) in data.hidden_fields ?? []"
        :key="`h${i}`"
        type="hidden"
        :name="hidden.name"
        :value="hidden.value ?? ''"
      >

      <div
        v-for="(field, i) in fields"
        :key="i"
      >
        <label
          v-if="field.type !== 'checkbox'"
          class="mb-1.5 block text-sm font-medium"
          :for="`f-${i}-${field.name}`"
        >
          {{ field.label }}<span
            v-if="field.required"
            aria-hidden="true"
          >&nbsp;*</span>
        </label>

        <textarea
          v-if="field.type === 'textarea'"
          :id="`f-${i}-${field.name}`"
          :name="field.name"
          :required="field.required"
          :placeholder="field.placeholder ?? ''"
          rows="5"
          :class="inputClass"
        />

        <select
          v-else-if="field.type === 'select'"
          :id="`f-${i}-${field.name}`"
          :name="field.name"
          :required="field.required"
          :class="inputClass"
        >
          <option
            value=""
            :disabled="field.required"
          >
            {{ field.placeholder || 'Choose…' }}
          </option>
          <option
            v-for="(option, oi) in field.options ?? []"
            :key="oi"
            :value="option"
          >
            {{ option }}
          </option>
        </select>

        <label
          v-else-if="field.type === 'checkbox'"
          class="flex items-start gap-2 text-sm"
        >
          <input
            :id="`f-${i}-${field.name}`"
            type="checkbox"
            :name="field.name"
            :required="field.required"
            value="yes"
            class="mt-1 shrink-0"
          >
          <span>{{ field.label }}</span>
        </label>

        <input
          v-else
          :id="`f-${i}-${field.name}`"
          :name="field.name"
          :type="field.type"
          :required="field.required"
          :placeholder="field.placeholder ?? ''"
          :class="inputClass"
        >
      </div>

      <label
        v-if="data.consent_text"
        class="flex items-start gap-2 text-sm text-[var(--site-muted)]"
      >
        <input
          type="checkbox"
          name="consent"
          required
          value="yes"
          class="mt-1 shrink-0"
        >
        <span>{{ data.consent_text }}</span>
      </label>

      <button
        type="submit"
        class="site-btn-accent w-full px-6 py-2.5 font-semibold transition"
      >
        {{ data.button_text || 'Send' }}
      </button>
    </form>

    <p
      v-if="submitted && data.success_text"
      class="mt-3 text-center text-sm text-[var(--site-muted)]"
    >
      {{ data.success_text }}
    </p>
  </div>
</template>
